import {Injectable} from '@angular/core';
import {OrganizationData, RegionData, WorldData} from '../../../types/nso-data-portal/entity-data';
import {OrganizationInformationRestService} from './organization-information-rest.service';
import {SearchEntityLevels} from '../../../types/nso-data-portal/search-entity-levels';
import {RegionInformationRestService} from './region-information-rest.service';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {GraphData, GraphRequestOptions} from '../../../types/nso-data-portal/census-data';
import {GsatData, ProjectCountData} from '../../../types/nso-data-portal/dashboard-data';
import {WorldInformationRestService} from './world-information-rest.service';
import {ChartRestService} from './chart-rest-service';
import {BaseRestService} from '../../common/BaseRestService';
import {ChartServiceRoutes} from '../../../types/nso-data-portal/charts/chart-service-routes';

interface EntityOptions {
  level?: SearchEntityLevels;
  entityId?: number;
  detailed?: boolean;
  options?: GraphRequestOptions;
}

interface ExportOptions {
  entityId?: number;
  yearFrom?: number;
  yearTo?: number;
  entityRestLevel?: SearchEntityLevels;
  entityExportChoiceLevel?: SearchEntityLevels;
}

@Injectable()
export class EntityInformationRestService extends BaseRestService {

  constructor(private organizationRestService: OrganizationInformationRestService,
              private regionRestService: RegionInformationRestService,
              private worldRestService: WorldInformationRestService,
              private chartRestService: ChartRestService) {
    super('');
  }

  getEntityInformation(searchData) {
    switch (searchData.level) {
      case SearchEntityLevels.ORGANIZATION:
        return this.organizationRestService.getOrganizationInformation(searchData.id).pipe(map(
          (res: OrganizationData) => this.saveToLocalStorage(res, searchData)));
      case SearchEntityLevels.REGION:
        return this.regionRestService.getRegionInformation(searchData.id).pipe(map(
          (res: RegionData) => this.saveToLocalStorage(res, searchData)));
      case SearchEntityLevels.WORLD:
      default:
        return this.worldRestService.getWorldInformation().pipe(map(
          (res: WorldData) => {
            res = {...res, id: 0};
            return this.saveToLocalStorage(res, searchData);
          }));
    }
  }

  getEntityGraphData(params: EntityOptions): Observable<GraphData> {
    const functionResolver = {
      [SearchEntityLevels.ORGANIZATION]: () => {
        return this.getEndpoint([ChartServiceRoutes.ORGANIZATION, String(params.options.id), 'census', 'censusGraphs']);
      },
      [SearchEntityLevels.REGION]: () => {
        return this.getEndpoint([ChartServiceRoutes.REGION, String(params.options.id), 'census', 'censusGraphs']);
      },
      [SearchEntityLevels.WORLD]: () => {
        return this.getEndpoint([ChartServiceRoutes.WORLD, 'census', 'censusGraphs']);
      }
    };

    params.options.route = functionResolver[params.level]();

    return params.detailed ? this.chartRestService.getDetailedCensusGraphs(params.options)
      : this.chartRestService.getCensusGraphs(params.options);
  }

  getProjectCount(params: EntityOptions): Observable<ProjectCountData> {

    const functionResolver = {
      [SearchEntityLevels.ORGANIZATION]: () => {
        return this.organizationRestService.getProjectCount(params.entityId);
      },
      [SearchEntityLevels.REGION]: () => {
        return this.regionRestService.getProjectCount(params.entityId);
      },
      [SearchEntityLevels.WORLD]: () => {
        return this.worldRestService.getProjectCount();
      }
    };

    return functionResolver[params.level]();
  }

  exportEntityHistory(params: ExportOptions) {
    const functionResolver = {
      [SearchEntityLevels.ORGANIZATION]: () => {
        return this.organizationRestService.getExport(params);
      },
      [SearchEntityLevels.REGION]: () => {
        return this.regionRestService.getExport(params);
      },
      [SearchEntityLevels.WORLD]: () => {
        return this.worldRestService.getExport(params);
      }
    };

    return functionResolver[params.entityRestLevel]();
  }


  getGsat(params: EntityOptions): Observable<GsatData> {

    const functionResolver = {
      [SearchEntityLevels.ORGANIZATION]: () => {
        return this.organizationRestService.getGsat(params.entityId);
      },
      [SearchEntityLevels.REGION]: () => {
        return this.regionRestService.getGsat(params.entityId);
      },
      [SearchEntityLevels.WORLD]: () => {
        return this.worldRestService.getGsat();
      }
    };

    return functionResolver[params.level]();
  }

  private saveToLocalStorage(data, searchData) {
    localStorage.setItem('portal_orgId', JSON.stringify({
      entity: data.id.toString(),
      entityLevel: searchData.level
    }));
    return data;
  }

}
